import { SyncLeboncoin_UpdateExternalListingMutation, SyncLeboncoin_UpdateExternalListingMutationVariables, SyncToLeboncoin_GetAdQuery, SyncToLeboncoin_GetAdQueryVariables } from "../../../codegen/graphql";
import client from "../../graphqlClient";
import { BetterError } from "../../../server/lib/BetterError";
import leboncoinPossibleLocations from "../../leboncoinPossibleLocations";
import { calculatePrices, calculatePriceWithCommissions } from "../../../common/pricesCalculation";
import { selectBestSupplierProductVariation } from "../../../common/selectBestSuplierProductVariation";
import { MediaRole, MediaType } from "../../../codegen/graphql";
import { checkLeboncoinListingVariationsConfigurationCompleteness, createListingConfigurationWithDefaults, filterVariationsByConfiguration, mergeVariationsConfiguration } from "../../../common/listingVariationsConfiguration";
import { getDescriptionTemplate } from "../../components/AdListing/getLBCdescriptionTemplate";
import { uploadMedias } from "./LeboncoinExtApi";
import { postMessageToLeboncoinExt } from "./LeboncoinExtApi";
import { gql } from "@apollo/client";
import Big from "big.js";
import inheritMarginOrTargetPrice from "../../../common/inheritMarginOrTargetPrice";
import { getShippingServicesForPackage } from "../../../common/shipping/getShippingServicesForPackage";
import { sendcloud_packAndCarry_ups } from "../../../common/shipping/FR/sendcloud_packAndCarry_UPS";
import { getTranslatedValue } from "../../../common/getTranslatedValue";


export function classifyAdCategory(query: string): Promise<any> {
  return postMessageToLeboncoinExt(window, {
    action: 'classify-ad-category',
    query
  });
}

export async function getAdFormConfig(categoryId: string): Promise<any> {
  if (sessionStorage.getItem('leboncoin_ad_form_config_'+categoryId)) {
    return JSON.parse(sessionStorage.getItem('leboncoin_ad_form_config_'+categoryId)!);
  }

  const config = await postMessageToLeboncoinExt(window, {
    action: 'get-ad-form-config',
    categoryId
  });

  sessionStorage.setItem('leboncoin_ad_form_config_'+categoryId, JSON.stringify(config));

  return config;
}

export function extractAdFormConfig(config: any) {
  const leboncoin_adParamsItems = config.navigation.ordered_steps.find((step: any) => (
    step.identifier === 'STEP_IDENTIFIER_ADPARAMS'
  ))!.ordered_items;

  const formConfig: {
    itemsDefinition: any;
    handlesVariations: boolean;
    formItems: any[];
    formItemsKeys: string[];
    variationsItemsKeys: string[];
    variationsItems: any[];
  } = {
    itemsDefinition: config.definitions.items,

    formItems: leboncoin_adParamsItems.map((identifier: any) => (
      config.definitions.items.find((itemDefinition: any) => (
        itemDefinition.identifier === identifier
      ))
    )),
    formItemsKeys: [],

    handlesVariations: false,
    variationsItemsKeys: [],
    variationsItems: [],
  };

  const leboncoin_variationsStep = config.navigation.ordered_steps.find((step: any) => (
    step.identifier === 'STEP_IDENTIFIER_VARIATION'
  ));

  if (leboncoin_variationsStep) {
    formConfig.handlesVariations = true;

    const leboncoin_variationsDefinition = config.definitions.items.find((item: any) => (
      leboncoin_variationsStep.ordered_items.includes(item.identifier)
    ));

    const leboncoin_variationsPrimaryKeys = leboncoin_variationsDefinition.entities.primary_associated_keys;

    // console.log({
    //   leboncoin_variationsStep,
    //   leboncoin_variationsDefinition,
    //   leboncoin_variationsPrimaryKeys,
    // })
    formConfig.variationsItems = formConfig.formItems.filter((definition: any) => (
      leboncoin_variationsPrimaryKeys.indexOf(definition.answer_modelization.representation.associated_key) !== -1
    ));

    formConfig.formItems = formConfig.formItems.filter((definition: any) => (
      leboncoin_variationsPrimaryKeys.indexOf(definition.answer_modelization.representation.associated_key) === -1
    ));

  }

  formConfig.formItemsKeys = formConfig.formItems.map((definition: any) => (
    definition.identifier
  ));

  formConfig.variationsItemsKeys = formConfig.variationsItems.map((definition: any) => (
    definition.identifier
  ));

  return formConfig;
}

export function getAdData({
  leboncoinUrl,
  leboncoinAdId,
}: {
  leboncoinUrl?: string,
  leboncoinAdId?: string,
}): Promise<any> {
  return postMessageToLeboncoinExt(window, {
    action: 'get-ad-data',
    leboncoinUrl,
    leboncoinAdId,
  });
}



export async function syncToLeboncoin(
  adId: string,
  externalListingId?: string,
) {
  // Get required data for syncing to Leboncoin
  const { data } = await client.query<
    SyncToLeboncoin_GetAdQuery,
    SyncToLeboncoin_GetAdQueryVariables
  >({
    query: SYNC_TO_LEBONCOIN,
    variables: { adId },
  });

  /* Checking config data */
  const ad = data.ad;
  if (!ad) {
    throw new BetterError('Ad not found', 404, { adId });
  }

  let leboncoinListings: NonNullable<SyncToLeboncoin_GetAdQuery['ad']>['externalListings'];
  if (externalListingId) {
    leboncoinListings = ad.externalListings.filter((listing) => (
      listing.id === externalListingId
    ));
  } else {
    leboncoinListings = ad.externalListings.filter((listing) => (
      listing.shop.shopType === 'leboncoin'
    ));
  }

  if (leboncoinListings.length === 0) {
    throw new BetterError('No Leboncoin listings found', 400, { adId });
  }

  if (leboncoinListings.some((listing) => !listing.externalData?.category)) {
    throw new BetterError('Leboncoin listing configuration not defined', 400, { adId });
  }

  let allUploadedImages: Record<string, {
    name: string;
    url: string;
  }> = {};
  for (const listing of leboncoinListings) {
    const leboncoinListingConfig = listing.externalData!;
    const listingConfiguration = listing.configuration;
    const listingConfigurationWithDefaults = createListingConfigurationWithDefaults(
      listingConfiguration,
      ad.variationTypes,
      ad.variations,
    )

    const postToLBCwithVariations = (
      leboncoinListingConfig.categoryHandlesVariations
      && listingConfigurationWithDefaults.isMultiVariation
    );

    if (postToLBCwithVariations === undefined) {
      throw new BetterError('Leboncoin multi-variation setting is not defined', 400, {
        listingId: listing.id,
        listingExternalId: listing.externalId,
      });
    }

    /*
    * Filtering which variations will be synced
    */
    const supplierType: 'FR_ilyes' | 'aliexpress' = listingConfigurationWithDefaults.supplierKey as 'FR_ilyes' | 'aliexpress';
    // Check if the variations configuration is valid
    if (!listingConfigurationWithDefaults.variationsConfig) {
      throw new BetterError('Leboncoin variations configuration is not defined', 400, {
        listingId: listing.id,
        listingExternalId: listing.externalId,
      });
    }

    const isLeboncoinVariationsConfigurationValid = (
      checkLeboncoinListingVariationsConfigurationCompleteness({
        configuration: listingConfigurationWithDefaults,
        types: ad.variationTypes,
        variations: ad.variations,
      })
    );

    if (!isLeboncoinVariationsConfigurationValid.isValid) {
      console.log(isLeboncoinVariationsConfigurationValid);
      throw new BetterError('Leboncoin variations configuration is not valid', 400, {
        listingId: listing.id,
        listingExternalId: listing.externalId,
      });
    }
    const filteredVariations = filterVariationsByConfiguration(
      ad.variations,
      listingConfigurationWithDefaults,
    );
    let variationsToSync: NonNullable<SyncToLeboncoin_GetAdQuery['ad']>['variations'] = [];

    // Single variation
    if (postToLBCwithVariations === false) {
      const selectedVariation = ad.variations.find((variation) => (
        variation.id === listingConfigurationWithDefaults.useSingleVariationId
      ));

      if (selectedVariation === undefined) {
        throw new Error('Please select a single variation for this ad. useSingleVariationId is not defined or invalid');
      }

      variationsToSync = [selectedVariation];
    }
    // Multi-variation
    else {
      // Filter variations using the configuration
      variationsToSync = filteredVariations

      if (variationsToSync.length === 0) {
        throw new BetterError('No variations to sync', 400, {
          listingId: listing.id,
          listingExternalId: listing.externalId,
        });
      }
    }

    // Now, we need to take care of supplier types
    // Because delivery conditions will change depending on the supplier type
    // Selecting only one supplier type for Leboncoin
    variationsToSync = variationsToSync.filter((variation) => (
      variation.supplierProductVariations.some((supplierProductVariation) => (
        supplierProductVariation.supplierProduct.supplierName === supplierType
      ))
    ));

    // Extract main images
    const PRIMARY_IMAGE = ad.medias.find((media) => (
      media.roles.includes(MediaRole.PrimaryImage) && media.type === MediaType.Image
    ));

    const AD_MAIN_MEDIAS = ad.medias.filter((media) => (
      media.roles.includes(MediaRole.Main) && media.type === MediaType.Image
    ));

    /*
    * Formatting variations data before posting on LBC
    */
    const variationsFormatted: {
      id: 'main' | string;
      price: number;
      quantity: number;
      variationAttributes?: any;
      variationTitle?: string;
      weight: number;
      packaging: any;
      images: (
        NonNullable<SyncToLeboncoin_GetAdQuery['ad']>['medias']
        | NonNullable<SyncToLeboncoin_GetAdQuery['ad']>['variations'][number]['variationTypeValues'][number]['medias']
      );
    }[] = variationsToSync.map((variation) => {
      const typeValueImages = variation.variationTypeValues.find(variationTypeValue => (
        variationTypeValue.type.id === ad.typeIdUsedForImages
      ))

      const supplierProductVariation = selectBestSupplierProductVariation(
        supplierType,
        listing.lang,
        variation.supplierProductVariations,
      );

      if (supplierProductVariation === undefined) {
        throw new Error('No supplier product variation found for this variation. Should not happen, SKU: ' + variation.id);
      }

      let variationImages = [];
      if (typeValueImages?.medias) {
        variationImages.push(...typeValueImages.medias);
      }
      variationImages.push(...AD_MAIN_MEDIAS);
      variationImages = variationImages
        .filter((image, index, array) => (
          image?.url && array.findIndex((i) => i?.url === image?.url) === index
        ));

      // TODO: add more info on price including VAT on the supplier product variation
      const calculatedCost = supplierProductVariation!.calculatedCost.find((cc) => (
        cc.destinationCountry === listing.lang
      ));

      const availableStock = supplierProductVariation!.availableStock.find((cc) => (
        cc.destinationCountry === listing.lang
      ))?.availableStock ?? 0;

      const marginOrTargetPrices = inheritMarginOrTargetPrice({
        listing: listing,
        listingConfiguration: listingConfigurationWithDefaults,
        variation: variation,
        ad: ad,
      });

      let finalPrice = calculatePrices({
        marginPercentOnBaseCost: marginOrTargetPrices.selected.marginPercentOnBaseCost,
        targetListingPriceIncludingVat: marginOrTargetPrices.selected.targetListingPriceIncludingVat,
        listingPriceRoundingMethod: marginOrTargetPrices.selected.listingPriceRoundingMethod,
        productCostExcludingVAT: calculatedCost?.totalCostExcludingVAT ?? 100,
        packingCostExcludingVAT: supplierProductVariation!.packaging.cost ?? 0,
        forPlatform: 'leboncoin',
        supplierName: supplierProductVariation!.supplierName,
        origin: 'FR',
        destination: listing.lang,
        packageWeight: supplierProductVariation!.packaging.weight,
        packageSize1: supplierProductVariation!.packaging.size1,
        packageSize2: supplierProductVariation!.packaging.size2,
        packageSize3: supplierProductVariation!.packaging.size3,
      })
      .setExternalPrice.round(2).times(100).toNumber();

      // Catch if finalPrice is 0, because it can't update the ad with a price of 0
      if (Number(finalPrice) === 0) {
        finalPrice = 10000;
      }
      
      return {
        id: variation.id,
        price: finalPrice,
        quantity: Math.max(availableStock, 0),
        images: variationImages.slice(0, 5),
        variationAttributes: reduceVariationAttributes(variation),
        variationTitle: getVariationTitle(variation),
        weight: supplierProductVariation!.packaging.weight,
        packaging: supplierProductVariation!.packaging,
      };
    });

    /*
    * Uploading images
    */
    const imagesDedupMarker: Record<string, boolean> = {};
    const imagesDedup = variationsFormatted.flatMap(variation => variation.images)
      .filter((media) => {
        if (imagesDedupMarker[media.id]) {
          return false;
        }
        if (allUploadedImages[media.id]) {
          return false;
        }
        imagesDedupMarker[media.id] = true;
        return true;
      });
    const newUploadedImagesMapById = await uploadMedias(
      imagesDedup,
    );
    allUploadedImages = {
      ...allUploadedImages,
      ...newUploadedImagesMapById,
    };

    function getMappedLeboncoinImage(media: any) {
      return allUploadedImages[media.id];
    }

    function reduceVariationAttributes(variation: NonNullable<SyncToLeboncoin_GetAdQuery['ad']>['variations'][number]) {
      return variation.variationTypeValues.reduce((acc: any, variationTypeValue) => {
        return {
          ...acc,
          ...listingConfiguration!.variationsConfig!.values![variationTypeValue.id]!.extraData,
        };
      }, {});
    }

    function getVariationTitle(
      variation: NonNullable<SyncToLeboncoin_GetAdQuery['ad']>['variations'][number],
      withTitle: boolean = true,
    ) {
      const lang = listing.lang;
      const variationAttributesTitle = variation.variationTypeValues.map((variationTypeValue) => (
        getTranslatedValue(variationTypeValue.value, lang)
      )).join(', ');

      const adTitle = getTranslatedValue(ad!.title, lang);
      return `${withTitle ? `${adTitle} - ` : ''}${variationAttributesTitle}`;
    }


    let leboncoinData;
    let leboncoinMappings: ReturnType<typeof extractMappings> | undefined;
    if (listing.externalId) {
      leboncoinData = await postMessageToLeboncoinExt(window, {
        action: 'get-ad-data',
        leboncoinAdId: listing.externalId,
      });

      // Extract mappings for each listing
      leboncoinMappings = extractMappings(leboncoinData);
    }

    const existingMainVariationMapping = Object.entries(leboncoinMappings || {})
      .find(([variationId, leboncoinVariationId]) => (
        leboncoinVariationId === 'main'
      ));

    const mainVariation = (
      existingMainVariationMapping
      && variationsFormatted.find((variation) => (
        variation.id.slice(0, 30) === existingMainVariationMapping[0]
      ))
    ) ?? variationsFormatted[0];

    const otherVariations = variationsFormatted.filter((variation) => (
      variation.id !== mainVariation.id
      && leboncoinMappings?.[variation.id.slice(0, 30)] !== 'main' // just in case
    ));

    const livraisonTitlePart = (() => {
      if (supplierType === 'FR_ilyes') {
        if (listing.externalData.location === 'toulouse') {
          return ' - Livraison & Retrait';
        }
        else {
          return ' - Livraison 3 jours relais';
        }
      } else if (supplierType === 'aliexpress') {
        return ' - Livraison gratuite';
      }
      return '';
    })();

    const shippingTypes = (() => {
      if (supplierType === 'FR_ilyes') {
        const opts = ['mondial_relay', 'shop2shop', 'colissimo', 'relais_colis_dropoff', 'distance'];
        if (listing.externalData.location === 'toulouse') {
          opts.unshift('click_and_collect');
        }
        return opts;
      } else if (supplierType === 'aliexpress') {
        return ['distance'];
      }
    })();

    const shippingCost = (() => {
      if (supplierType === 'FR_ilyes') {
        const shippingServices = getShippingServicesForPackage({
          onlyServices: [sendcloud_packAndCarry_ups],
          packageWeight: mainVariation.weight,
          packageSize1: mainVariation.packaging.size1,
          packageSize2: mainVariation.packaging.size2,
          packageSize3: mainVariation.packaging.size3,
          orderPlatform: 'leboncoin',
          supplier: supplierType,
          origin: 'FR',
          destination: listing.lang,
        });
        return new Big(shippingServices[0]!.rate.priceWithVAT)
          .times(2)
          .round(0, Big.roundUp)
          .div(2)
          .times(100)
          .toNumber();
      } else if (supplierType === 'aliexpress') {
        return 0;
      }
      return 0;
    })();

    const lbcWeight = (() => {
      let possibleUpperWeights = [
        100,
        250,
        500,
        1000,
        2000,
        5000,
        10000,
        20000,
        30000,
        40000
      ];
      if (supplierType === 'FR_ilyes') {
        return possibleUpperWeights.find((weight) => (
          weight >= mainVariation.weight
        )) ?? mainVariation.weight;
      } else if (supplierType === 'aliexpress') {
        return 1000;
      }
    })();

    const title = ((
      mainVariation.variationTitle === undefined
      || !(listing.configuration as any)?.addVariationTitle
    )
      ? getTranslatedValue(ad.title, listing.lang)
      : mainVariation.variationTitle
    ) + livraisonTitlePart;

    const additionalVariantsDescription = (() => {
      let text = `\n\n∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎∎\n\nVariantes disponibles :\n`;

      ad.variations.forEach((variation) => {
        const supplierProductVariation = selectBestSupplierProductVariation(
          supplierType,
          listing.lang,
          variation.supplierProductVariations,
        );
        if (!supplierProductVariation) {
          return;
        }
        const marginOrTargetPrices = inheritMarginOrTargetPrice({
          listing: listing,
          listingConfiguration: listingConfigurationWithDefaults,
          variation: variation,
          ad: ad,
        });
        const calculatedCost = supplierProductVariation!.calculatedCost.find((cc) => (
          cc.destinationCountry === listing.lang
        ))!;
        const price = calculatePrices({
          marginPercentOnBaseCost: marginOrTargetPrices.selected.marginPercentOnBaseCost,
          targetListingPriceIncludingVat: marginOrTargetPrices.selected.targetListingPriceIncludingVat,
          listingPriceRoundingMethod: marginOrTargetPrices.selected.listingPriceRoundingMethod,
          productCostExcludingVAT: calculatedCost!.totalCostExcludingVAT,
          packingCostExcludingVAT: supplierProductVariation!.packaging.cost ?? 0,
          forPlatform: 'leboncoin',
          supplierName: supplierProductVariation!.supplierName,
          origin: 'FR',
          destination: listing.lang,
          packageWeight: supplierProductVariation!.packaging.weight,
          packageSize1: supplierProductVariation!.packaging.size1,
          packageSize2: supplierProductVariation!.packaging.size2,
          packageSize3: supplierProductVariation!.packaging.size3,
        })
          .listingPriceIncludingVAT.toFixed(2);

        if (Number(price) === 0) {
          return;
        }
        text += `- ${getVariationTitle(variation, false)} : ${price}€\n`;
      });
      text += '\n';
      return text;
    })();

    const leboncoinAdData = {
      category_id: leboncoinListingConfig.category?.id,
      ad_type: "sell",
      email: "shop@wide-like-a-river.com",
      phone: "+33751360303",
      // tracking_dd: "dd:lbc.adlife.abtest.genaidescription:|lbc.adlife.abtest.photo.first:|lbc.adlife.abtest.makes.models.vehicle.private.mandatory:|rollout:true",
      subject: title,
      body: getDescriptionTemplate({
        title,
        description: (ad.descriptionLeboncoin || getTranslatedValue(ad.description, listing.lang) || '') + additionalVariantsDescription,
        supplierType,
        forLocationId: listing.externalData.location,
      }),
      images: mainVariation.images.map(getMappedLeboncoinImage),
      // TODO: restore all locations?
      location: leboncoinPossibleLocations.toulouse,
      phone_hidden: false,
      no_salesmen: true,
  
      price_cents: mainVariation.price,
      extended_attributes: {
        shipping: {
          estimated_parcel_weight: lbcWeight,
          shipping_types: shippingTypes,
          version: 2,
        }
      },
      attributes: {
        ...leboncoinListingConfig.mainAttributes,
        ...(mainVariation.variationAttributes ?? {}),

        quantity: (mainVariation.quantity > 30 ? 30 : mainVariation.quantity).toString(),
        shipping_cost: shippingCost.toString(),
        custom_ref: mainVariation.id.substring(0, 30),

        // We keep the existing price_reco if possible
        // Otherwise it will be created by the Add-on extension
        price_reco: leboncoinData?.attributes.price_reco,
        title_adparams_prediction_id: leboncoinData?.attributes.title_adparams_prediction_id,

        category_reco_prediction_id: leboncoinListingConfig.category?.category_reco_prediction_id,
      },
      variations: otherVariations.length > 0
        ? otherVariations.map((variation) => ({
            variation_id: leboncoinMappings?.[variation.id.slice(0, 30)] || undefined,
            price_cents: variation.price,
            images: variation.images.map(getMappedLeboncoinImage),
            extended_attributes: {},
            attributes: {
              custom_ref: variation.id.substring(0, 30),
              quantity: (variation.quantity > 30 ? 30 : variation.quantity).toString(),
              ...variation.variationAttributes,
            },
          }))
        : undefined,
    }

    // console.log('leboncoinAdData', leboncoinAdData);
    // return;

    if (listing.externalId) {
      await postMessageToLeboncoinExt(window, {
        action: 'update-ad',
        adData: leboncoinAdData,
        adId: listing.externalId,
      });

      await client.mutate({
        mutation: UPDATE_EXTERNAL_LISTING,
        variables: {
          id: listing.id,
          input: {
            externalData: {
              location: listing.externalData.location,
              handledVariations: variationsFormatted.map((variation) => variation.id),
            }
          },
        },
      })
    } else {
      const leboncoinAdId = await postMessageToLeboncoinExt(window, {
        action: 'post-ad',
        adData: leboncoinAdData,
      });

      await client.mutate<
        SyncLeboncoin_UpdateExternalListingMutation,
        SyncLeboncoin_UpdateExternalListingMutationVariables
      >({
        mutation: UPDATE_EXTERNAL_LISTING,
        variables: {
          id: listing.id,
          input: {
            externalId: String(leboncoinAdId),
            externalData: {
              location: listing.externalData.location,
              handledVariations: variationsFormatted.map((variation) => variation.id),
            },
          },
        },
      })
    }
  }
}

const SYNC_TO_LEBONCOIN = gql`
  query SyncToLeboncoin_getAd($adId: ID!) {
    ad(id: $adId) {
      id
      title
      description
      descriptionLeboncoin
      percentageFee
      withVariations
      typeIdUsedForImages
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
      listingPriceRoundingMethod
      medias {
        id
        url
        roles
        type
      }
      variationTypes {
        id
        type
        typeValues {
          id
          value
        }
      }
      externalListings {
        id
        shop {
          shopType
        }
        lang
        externalId
        configuration {
          supplierKey
          isMultiVariation
          useSingleVariationId
          listingPriceRoundingMethod
          marginPercentOnBaseCost
          targetListingPriceIncludingVat
          variationsConfig {
            types
            values
            variations
          }
        }
        externalData
      }
      variations {
        id
        marginPercentOnBaseCost
        targetListingPriceIncludingVat
        listingPriceRoundingMethod
        supplierProductVariations {
          supplierName
          calculatedCost {
            destinationCountry
            totalCostIncludingVAT
            totalCostExcludingVAT
            totalCostVAT
            VATRate
          }
          availableStock {
            destinationCountry
            availableStock
          }
          sku
          packaging
          supplierProduct {
            id
            name
            supplierName
          }
        }
        variationTypeValues {
          id
          value
          type {
            id
            type
          }
          medias {
            id
            url
            roles
            type
          }
        }
      }
    }
  }
`

function extractMappings(leboncoinListingData: any) {
  // Maps our variation ids to the leboncoin variation ids
  let mappings: {
    [variationId: string]: string | null;
  } = {};

  const mainVariationId = leboncoinListingData.attributes.custom_ref;

  if (mainVariationId) {
    mappings[mainVariationId.slice(0, 30)] = "main";
  }

  if (leboncoinListingData.variations) {
    leboncoinListingData.variations.forEach((variation: any) => {
      const variationId = variation.attributes.custom_ref;
      if (variationId) {
        mappings[variationId.slice(0, 30)] = variation.variation_id;
      }
    });
  }

  return mappings;
}



const UPDATE_EXTERNAL_LISTING = gql`
  mutation SyncLeboncoin_UpdateExternalListing($id: ID!, $input: UpdateAdExternalListingInput!) {
    updateAdExternalListing(id: $id, input: $input) {
      id
      externalId
    }
  }
`